import type { TrackEventMessage } from "@sinch/bus";
import { nanoid } from "nanoid";
import type { TrackEventPayload } from "./types";

const getShadowTarget = (event: Event): HTMLElement | null => {
  const eventPath = event.composedPath();
  const targetElement = eventPath[0];
  if (targetElement instanceof HTMLElement) {
    return targetElement;
  }
  return null;
};

const getValidTarget = (event: Event): HTMLElement | null => {
  const target = getShadowTarget(event);
  const dataType =
    target?.attributes.getNamedItem("data-event-name") ||
    target?.attributes.getNamedItem("data-event-type");
  if (dataType) {
    return target;
  }
  return null;
};

const getAppMfeName = (event: Event): string | null => {
  const eventPath = event.composedPath();
  const mfeParentContainer = eventPath.find(
    (target) =>
      target instanceof HTMLElement &&
      target.attributes.getNamedItem("data-app")
  );
  return (
    (mfeParentContainer as HTMLElement)?.attributes?.getNamedItem("data-app")
      ?.value || null
  );
};

export const formatEasyClickEvent = (
  easyClickEvent: Event
): TrackEventPayload | null => {
  const target = getValidTarget(easyClickEvent);
  const parentMfeAppName = getAppMfeName(easyClickEvent);

  if (!target) {
    return null;
  }

  const dataName = target.attributes.getNamedItem("data-event-name");
  const dataType = target.attributes.getNamedItem("data-event-type");
  const dataAppName = target.attributes.getNamedItem("data-event-app");
  const dataTag = target.attributes.getNamedItem("data-event-tag");
  const dataPurposes = target.attributes.getNamedItem("data-event-purposes");
  const dataDesc = target.attributes.getNamedItem("data-event-description");

  const type = dataType?.value;
  const source = dataType?.baseURI;
  const tag = dataTag?.value;
  const appName = dataAppName?.value || parentMfeAppName;
  const purposes = dataPurposes?.value.split(",");
  const action = "click";
  const description = dataDesc?.value;

  return {
    id: nanoid(),
    type,
    source: source || "",
    tag,
    appName: appName || "",
    name: dataName?.value || "",
    purposes: (purposes || []) as TrackEventMessage["payload"]["purposes"],
    action,
    description,
  };
};
